import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { Form, FormGroup, Checkbox, RadioGroup } from 'components/common/form';
import { TaskPointWeightSelector } from 'components/common/tasks';
import { EditorBlockTranslationReference } from 'components/editor';
import {
  defaultBlockData,
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo
} from 'components/editor/blocks';
import { TextEditor } from 'components/editor/inputs';

import type { FormApi } from 'informed';
import type { BlockOpenQuestion } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

type FormState = {
  allowEditing: boolean;
  answertypes: {
    text: boolean;
    file: boolean;
  };
  filetype: BlockOpenQuestion['filetype'];
  grading: BlockOpenQuestion['grading'];
  gradingType: BlockOpenQuestion['gradingType'];
  show_answers_after_submission: boolean;
};

const EditorBlockOpenQuestion = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockOpenQuestion> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();
  const formApi = useRef<FormApi<FormState> | null>(null);

  // update form state when data props change
  useEffect(() => {
    if (!formApi.current) return;
    const formState = formApi.current.getState().values;

    const formStateTypes = [
      ...(formState.answertypes && formState.answertypes.text ? ['text'] : []),
      ...(formState.answertypes && formState.answertypes.file ? ['file'] : [])
    ];
    if (JSON.stringify(data.answertypes) !== JSON.stringify(formStateTypes)) {
      formApi.current.setValue(
        'answertypes.text',
        data.answertypes.includes('text')
      );
      formApi.current.setValue(
        'answertypes.file',
        data.answertypes.includes('file')
      );
    }

    if (data.filetype !== formState.filetype) {
      formApi.current.setValue('filetype', data.filetype);
    }
    if (data.grading !== formState.grading) {
      formApi.current.setValue('grading', data.grading);
    }
    if (
      data.show_answers_after_submission !==
      formState.show_answers_after_submission
    ) {
      formApi.current.setValue(
        'show_answers_after_submission',
        data.show_answers_after_submission
      );
    }
  }, [
    data.answertypes,
    data.filetype,
    data.grading,
    data.show_answers_after_submission
  ]);

  const handleQuestionChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            question: html
          }
        }
      }
    });
  };

  const handleAllowEditingChange = (value: boolean) => {
    if (data.allowEditing !== value) {
      updateData({ ...data, allowEditing: value });
    }
  };

  const handleAnswerTypeTextChange = (value: boolean) => {
    if (!formApi.current) return;

    const currentValues = formApi.current.getState().values;
    const newTypes: BlockOpenQuestion['answertypes'] = [];

    if (value) {
      newTypes.push('text');
    }
    if (currentValues.answertypes && currentValues.answertypes.file) {
      newTypes.push('file');
    }
    if (JSON.stringify(newTypes) !== JSON.stringify(data.answertypes)) {
      updateData({ ...data, answertypes: newTypes });
    }
  };

  const handleAnswerTypeFileChange = (value: boolean) => {
    if (!formApi.current) return;

    const currentValues = formApi.current.getState().values;
    const newTypes: BlockOpenQuestion['answertypes'] = [];

    if (currentValues.answertypes && currentValues.answertypes.text) {
      newTypes.push('text' as const);
    }
    if (value) {
      newTypes.push('file' as const);
    }
    if (JSON.stringify(newTypes) !== JSON.stringify(data.answertypes)) {
      updateData({ ...data, answertypes: newTypes });
    }
  };

  const handleFileTypeChange = (value: BlockOpenQuestion['filetype']) => {
    if (data.filetype !== value) {
      updateData({ ...data, filetype: value });
    }
  };

  const handleGradingChange = (value: BlockOpenQuestion['grading']) => {
    const newData: BlockOpenQuestion = { ...data, grading: value };

    if (value === 'peer') {
      newData.show_answers_after_submission = true;
      if (formApi.current) {
        formApi.current.setValue('show_answers_after_submission', true);
      }
    }
    if (data.grading !== value) updateData(newData);
  };

  const handleGradingTypeChange = (value: BlockOpenQuestion['gradingType']) => {
    if (data.gradingType !== value) {
      updateData({ ...data, gradingType: value });
    }
  };

  const handleShowAnswersChange = (value: boolean) => {
    if (data.show_answers_after_submission !== value) {
      updateData({ ...data, show_answers_after_submission: value });
    }
  };

  const handleFeedbackChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedback: html
          }
        }
      }
    });
  };

  const handlePointsWeightChange = (value: number) => {
    updateData({ ...data, pointsWeight: value });
  };

  const setFormApi = (api: FormApi<FormState>) => {
    formApi.current = api;
  };

  const pointsWeight = data.hasOwnProperty('pointsWeight')
    ? data.pointsWeight
    : defaultBlockData.openquestion.pointsWeight;

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="openquestion">
      <Form
        getApi={setFormApi}
        initialValues={
          {
            allowEditing: data.allowEditing || false,
            answertypes: {
              text: data.answertypes.includes('text'),
              file: data.answertypes.includes('file')
            },
            filetype: data.filetype,
            grading: data.grading,
            gradingType: data.gradingType || 'text-and-stars',
            show_answers_after_submission: data.show_answers_after_submission
          } satisfies FormState
        }
      >
        {({ formState }) => (
          <div>
            <FormGroup maxWidth="100%">
              {!isAIPreview ? (
                <TaskPointWeightSelector
                  blockId={blockId}
                  blockName={messages.blocksOpenQuestion}
                  disabled={isPreview}
                  isMandatory={data.isMandatory}
                  pointsWeight={pointsWeight}
                  onSelection={handlePointsWeightChange}
                />
              ) : null}

              {isAIPreview || isPreview ? (
                <RichTextValue
                  html={data.locales[languageVersion].translatable.question}
                />
              ) : (
                <>
                  <EditorBlockTranslationReference
                    backgroundColor={theme.lightGrey}
                    isInnerElement={true}
                    referenceHtml={
                      data.locales[editorLanguageReference]?.translatable
                        .question || ''
                    }
                    style={{ marginBottom: 5 }}
                    translationHtml={
                      data.locales[languageVersion].translatable.question
                    }
                  >
                    <TextEditor
                      ariaLabel={messages.commonQuestion}
                      backgroundColor="inherit"
                      content={
                        data.locales[languageVersion].translatable.question
                      }
                      onChange={handleQuestionChange}
                      padding="0"
                      placeholder={messages.commonAddQuestion}
                    />
                  </EditorBlockTranslationReference>
                </>
              )}
            </FormGroup>

            {!isAIPreview ? (
              <>
                <FormGroup
                  label={messages.editorBlockOpenQuestionAnswerTypes}
                  maxWidth="100%"
                  style={{ marginTop: 25 }}
                >
                  <Checkbox
                    disabled={isPreview || isEditingLanguageVersion}
                    field="answertypes.text"
                    id={`open-question-text-${blockId}`}
                    label={messages.editorBlockOpenQuestionText}
                    onValueChange={handleAnswerTypeTextChange}
                    value="text"
                  />
                  <Checkbox
                    disabled={isPreview || isEditingLanguageVersion}
                    field="answertypes.file"
                    id={`open-question-file-${blockId}`}
                    label={messages.commonFile}
                    onValueChange={handleAnswerTypeFileChange}
                    style={{ marginTop: 5 }}
                  />
                  <RadioGroup
                    field="filetype"
                    onChange={handleFileTypeChange}
                    options={[
                      {
                        disabled:
                          isPreview ||
                          isEditingLanguageVersion ||
                          !formState.values.answertypes?.file,
                        id: `open-question-file-any-${blockId}`,
                        label: messages.editorBlockOpenQuestionFileAny,
                        value: 'any'
                      },
                      {
                        disabled:
                          isPreview ||
                          isEditingLanguageVersion ||
                          !formState.values.answertypes?.file,
                        id: `open-question-file-pdf-${blockId}`,
                        label: messages.editorBlockOpenQuestionFilePdf,
                        value: 'pdf'
                      },
                      {
                        disabled:
                          isPreview ||
                          isEditingLanguageVersion ||
                          !formState.values.answertypes?.file,
                        id: `open-question-file-image-${blockId}`,
                        label: messages.editorBlockOpenQuestionFileImage,
                        value: 'image'
                      },
                      {
                        disabled:
                          isPreview ||
                          isEditingLanguageVersion ||
                          !formState.values.answertypes?.file,
                        id: `open-question-file-video-${blockId}`,
                        label: messages.editorBlockOpenQuestionFileVideo,
                        value: 'video'
                      }
                    ]}
                    style={{ margin: '5px 0 0 20px' }}
                  />
                </FormGroup>

                <FormGroup
                  label={messages.editorBlockOpenQuestionEditingAnswer}
                >
                  <Checkbox
                    disabled={isPreview || isEditingLanguageVersion}
                    field="allowEditing"
                    id={`allow-editing-${blockId}`}
                    label={messages.editorBlockOpenQuestionAllowEditing}
                    labelTooltip={
                      messages.editorBlockOpenQuestionAllowEditingTooltip
                    }
                    onValueChange={handleAllowEditingChange}
                  />
                </FormGroup>
              </>
            ) : null}

            <FormGroup label={messages.commonFeedback} maxWidth="100%">
              {isAIPreview || isPreview ? (
                <RichTextValue
                  html={
                    data.locales[languageVersion].translatable.feedback || '-'
                  }
                  style={{
                    backgroundColor: 'white',
                    border: `1px solid ${theme.lightGreyDark}`,
                    borderRadius: theme.inputBorderRadius,
                    color: theme.textColorLight,
                    padding: 10
                  }}
                />
              ) : (
                <>
                  <EditorBlockTranslationReference
                    backgroundColor={theme.lightGrey}
                    isInnerElement={true}
                    referenceHtml={
                      data.locales[editorLanguageReference]?.translatable
                        .feedback || ''
                    }
                    style={{ marginTop: 2, marginBottom: 5 }}
                    translationHtml={
                      data.locales[languageVersion].translatable.feedback
                    }
                  >
                    <TextEditor
                      content={
                        data.locales[languageVersion].translatable.feedback
                      }
                      onChange={handleFeedbackChange}
                      placeholder={messages.commonFeedbackPlaceholder}
                      showBorder={true}
                    />
                  </EditorBlockTranslationReference>
                </>
              )}
            </FormGroup>

            {!isAIPreview && !editorLanguageActive ? (
              <EditorBlockMoreSettings
                settingsHaveBeenEdited={
                  !isPreview &&
                  Boolean(
                    data?.grading !== 'none' ||
                      data?.isMandatory ||
                      data?.show_answers_after_submission
                  )
                }
              >
                <EditorBlockIsMandatorySettings
                  blockId={blockId}
                  data={data}
                  disabled={isPreview}
                  updateData={updateData}
                />

                <FormGroup
                  label={messages.editorBlockOpenQuestionGrading}
                  labelHelpText={
                    formState?.values?.grading !== 'none' &&
                    pointsWeight === 0 ? (
                      <span>
                        <Icon
                          icon="exclamation-triangle"
                          style={{
                            fontSize: '1.1rem',
                            marginRight: 4,
                            position: 'relative',
                            top: -1
                          }}
                        />
                        {messages.editorBlockOpenQuestionGradingWarning}
                      </span>
                    ) : null
                  }
                  maxWidth="100%"
                >
                  <RadioGroup
                    field="grading"
                    onChange={handleGradingChange}
                    options={[
                      {
                        disabled: isPreview,
                        id: `open-question-grading-none-${blockId}`,
                        label: messages.editorBlockOpenQuestionGradingNone,
                        value: 'none'
                      },
                      {
                        disabled: isPreview,
                        id: `open-question-grading-manual-${blockId}`,
                        label: messages.editorBlockOpenQuestionGradingManual,
                        value: 'manual'
                      },
                      {
                        disabled: isPreview,
                        id: `open-question-grading-peer-${blockId}`,
                        label: messages.editorBlockOpenQuestionGradingPeer,
                        value: 'peer'
                      }
                    ]}
                  />
                  <Checkbox
                    disabled={isPreview || formState.values.grading === 'peer'}
                    field="show_answers_after_submission"
                    id={`open-question-grading-show-answers-${blockId}`}
                    label={messages.editorBlockOpenQuestionShowAnswers}
                    onValueChange={handleShowAnswersChange}
                    style={{ marginTop: 10 }}
                  />
                </FormGroup>

                <FormGroup
                  label={messages.editorBlockOpenQuestionGradingType}
                  style={
                    !formState?.values?.grading ||
                    formState?.values?.grading === 'none'
                      ? { display: 'none' }
                      : {}
                  }
                >
                  <RadioGroup
                    field="gradingType"
                    onChange={handleGradingTypeChange}
                    options={[
                      {
                        disabled: isPreview,
                        id: `open-question-grading-type-text-${blockId}`,
                        label: messages.editorBlockOpenQuestionGradingTypeText,
                        tooltip:
                          messages.editorBlockOpenQuestionGradingTypeTextInfo,
                        value: 'text'
                      },
                      {
                        disabled: isPreview,
                        id: `open-question-grading-type-star-${blockId}`,
                        label: messages.editorBlockOpenQuestionGradingTypeStar,
                        tooltip:
                          messages.editorBlockOpenQuestionGradingTypeStarInfo,
                        value: 'text-and-stars'
                      },
                      {
                        disabled: isPreview,
                        id: `open-question-grading-type-points-${blockId}`,
                        label:
                          messages.editorBlockOpenQuestionGradingTypePoints,
                        value: 'text-and-points'
                      }
                    ]}
                  />
                </FormGroup>
              </EditorBlockMoreSettings>
            ) : null}
          </div>
        )}
      </Form>

      {!isAIPreview ? (
        <EditorBlockRequireAnswerToProceedInfo data={data} />
      ) : null}

      <style jsx>{`
        .openquestion {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockOpenQuestion;
